<template>
  <div class="base-card">
    <div class="card-header" :class="{ 'spinner': spinnerOn }">
      <div class="card-name">Genomics Test Results</div>
      <div class="card-icon">
        <span class="material-symbols-outlined" @click="closeCenterColumn">close</span>
      </div>
    </div>
    <div class="header-container">
      <div class="items">
        <div class="header-top-first">
          <span class="specimen-type-text">{{ specimenType || '—' }}</span>
          <span class="genomics">Genomics</span>
        </div>
        <div class="header-top-second">
          <span>{{ testType || '—' }}</span>
        </div>
      </div>
      <div class="header-middle">
        <div class="items">
          <span class="label">Diagnosis Reported</span> <span>{{ diagnosis || '—' }}</span>
        </div>
        <div class="items">
          <span class="label">TMB</span> <span>{{ tmb }}</span>
        </div>
        <div class="items">
          <span class="label">MSI</span> <span>{{ msi }}</span>
        </div>
      </div>
      <div class="view-report">
        <a href="javascript:void(0)">View Report</a>
      </div>
    </div>
    <div class="genomics-data">
      <div class="variants-container">
        <div class="tier-tabs-container">
          <div class="tier-tabs" @click="tierButtonClick(1)" :class="{ 'tier-tab-active': tier1Selected }">
            <span class="material-symbols-outlined size-20">
              radio_button_{{ tierIcon1 }}
            </span>
            <span class="tier-text"> Tier 1</span>
          </div>
          <div class="tier-tabs" @click="tierButtonClick(2)" :class="{ 'tier-tab-active': tier2Selected }">
            <span class="material-symbols-outlined size-20">
              radio_button_{{ tierIcon2 }}
            </span>
            <span class="tier-text"> Tier 2</span>
          </div>
          <div class="tier-tabs" @click="tierButtonClick(3)" :class="{ 'tier-tab-active': tier3Selected }">
            <span class="material-symbols-outlined size-20">
              radio_button_{{ tierIcon3 }}
            </span>
            <span class="tier-text"> Tier 3</span>
          </div>
        </div>
        <div class="significance-label">Alteration(s) of <span class="tier-label">{{ tierLabel }}</span> Clinical
          Significance</div>
        <div v-if="loadingGenomics">
        </div>
        <div v-else class="alterations">
          <div v-for="alteration in displayAlterations" :key="alteration.id">
            <VariantCard :gene="alteration.gene" :alteration="alteration.alteration"
              :treatments-count="alteration.treatmentsCount" :trials-count="alteration.trialsCount"
              :exon="alteration.exon" :alt="alteration" class="variant-card"
              :class="{ 'variant-card-active': tier1Selected }">
            </VariantCard>
          </div>
        </div>
      </div>
      <div class="trials-container">
        <TreatmentsTrials :treatments="treatments" :trials="trials" :alterations="displayAlterations"
          :loadingGenomics="loadingGenomics" :stillFetchingVariants="spinnerOn"></TreatmentsTrials>
      </div>
    </div>

  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, ref, unref, toRaw, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import VariantCard from './VariantCard.vue'
import TreatmentsTrials from './TreatmentsTrials.vue'
import { getVariants, getAlterations, getTmbMsi, getTreatments, getTrials } from './getGenomicsData.js'
import { clearCenterColumn, closeCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'


const store = useStore()
const sampleId = computed(() => store.getters.session?.genomicsResults?.sampleId || '')
const sampleIdStored = ref('')
const diagnosis = computed(() => store.getters.session?.genomicsResults?.diagnosis || '')
const specimenType = computed(() => store.getters.session?.genomicsResults?.specimenType || '')
const testType = computed(() => store.getters.session?.genomicsResults?.testType || '')
const variants = ref([])
const alterations = ref([])
const displayAlterations = ref([])
const treatments = ref([])
const trials = ref([])
const loadingGenomics = ref(false)
const spinnerOn = ref(false)

const tmb = ref('No clinically significant TMB')
const msi = ref('No clinically significant MSI')
let altArray = [] //for temporary raw storage to go from alterations to displayAlterations

const tierIcon1 = ref('checked')
const tierIcon2 = ref('unchecked')
const tierIcon3 = ref('unchecked')

const tier1Selected = ref(true)
const tier2Selected = ref(false)
const tier3Selected = ref(false)
const tierLookup = { 1: tier1Selected, 2: tier2Selected, 3: tier3Selected }
const tierIconLookup = { 1: tierIcon1, 2: tierIcon2, 3: tierIcon3 }
const tierLabelLookup = { 1: 'Strong', 2: 'Potential', 3: 'Unknown' }

const tierLabel = ref(tierLabelLookup[1])
const clearTierTabs = () => {
  tier1Selected.value = false
  tier2Selected.value = false
  tier3Selected.value = false
}
const clearTierIcons = () => {
  tierIcon1.value = 'unchecked'
  tierIcon2.value = 'unchecked'
  tierIcon3.value = 'unchecked'
}

const tierButtonClick = (number) => {
  clearTierIcons()
  clearTierTabs()
  tierLookup[number].value = true
  tierIconLookup[number].value = 'checked'
  tierLabel.value = tierLabelLookup[number]
}

const loadAllGenomics = async () => {
  loadingGenomics.value = true
  spinnerOn.value = true
  variants.value = await getVariants(sampleId.value)
  // Note: filtering out cases where ref_allele == genome
  const filteredVariants = unref(variants).results.filter((variant) => variant.Ref_allele !== variant.Genotype)
  const variantToPromiseMap = new Map()
  const retryPromises = []
  const MAX_RETRIES = 3 // Maximum number of retries
  // clear out the alterations array
  alterations.value = []
  treatments.value = []
  trials.value = []

  // 'or..of' iterator compatible with async/await
  // however, this is a very serial/sequential wait approach
  // for (const variant of uVariants.results) {
  //   const alteration = await getAlterations(variant)
  //   alterations.value.push(alteration.results[0])
  // }

  // Promise style, so we can do these VEP calls
  // in parallel and get a speedup via Promise.allSettled
  const getAlterationsRetry = async (variant, retries) => {
    try {
      const response = await getAlterations(variant)
      return response
    } catch (error) {
      if (retries > 0) {
        console.log(`Retrying for variant ${variant}, retries left: ${retries}`)
        return getAlterationsRetry(variant, retries - 1)
      } else {
        throw error // Throw if all retries are exhausted
      }
    }
  }

  filteredVariants.forEach(variant => {
    const promise = getAlterationsRetry(variant, MAX_RETRIES)
    variantToPromiseMap.set(variant, promise)
  })

  const settleVepPromises = async (array) => {
    await Promise.allSettled(array)
      .then(results => {
        results.forEach((result, index) => {
          const variant = filteredVariants[index]
          if (result.status === 'fulfilled') {
            const response = result.value
            if (!response?.results?.length) { return }
            // we're getting 'annotation: failed' here, so retry
            if (response.results[0].annotation) {
              const retryPromise = getAlterationsRetry(variant, MAX_RETRIES)
              retryPromises.push(retryPromise)
            } else {
              alterations.value.push(response.results[0])
            }
          } else {
            console.log(result.status, result.reason)
          }
        })
      })
  }

  await settleVepPromises(Array.from(variantToPromiseMap.values()))
  // Wait for the additional retry promises to settle
  await settleVepPromises(retryPromises)

  await getTMBMSI()

  await loadDisplayData()
  loadingGenomics.value = false

  //get the first
  await getTreatmentsTrialsFirstAlteration()
  spinnerOn.value = false

  //get the rest
  await getTreatmentsTrialsSerial()
}

const loadDisplayData = async () => {
  clearCenterColumn('genomics')
  displayAlterations.value = []
  let nameArr = []
  let gene = ''
  let alt = ''
  let uAlterations = unref(alterations)
  altArray = toRaw(uAlterations)
  console.log('raw alterations Array: ', altArray)

  for (const alter of altArray) {
    if (!alter?.name) { continue }
    nameArr = alter?.name?.split(' ')
    gene = nameArr[0]
    alt = nameArr[1]

    const exon = alter?.exon || ''

    const obj = {
      gene: gene,
      alteration: recodeAa(alt),
      originalAlt: alt,
      exon: exon,
      treatmentsCount: 0,
      trialsCount: 0,
      treatmentRows: [],
      trialRows: [],
    }
    displayAlterations.value.push(obj)
  }
}
// //Promise.allSettled approach. Faster but can flood annotation
// const getTreatmentsTrialsParallel = async () => {
//   const promises = []
//   // TODO: replace with user input distance and patient location
//   const distance = '5000'
//   const location = '97005'
//   for (const alter of displayAlterations.value) {
//     promises.push(fetchTreatments(distance, location, alter, alter.gene, alter.originalAlt))
//     promises.push(fetchTrials(distance, location, alter, alter.gene, alter.originalAlt))
//   }
//   await Promise.allSettled(promises)
// }

const getTreatmentsTrialsSerial = async () => {
  // TODO: replace with user input distance and patient location
  const distance = '24000' //Note: leaving distance an empty string seems to be equivalent to 1000 mi
  const location = '97005'
  //skip the first because we've already done it
  for (const alter of displayAlterations.value.slice(1, displayAlterations.value.length)) {
    await fetchTreatments(distance, location, alter, alter.gene, alter.originalAlt)
    await fetchTrials(distance, location, alter, alter.gene, alter.originalAlt)
  }
}

const getTreatmentsTrialsFirstAlteration = async () => {
  const distance = '24000'
  const location = '97005'
  if (displayAlterations.value.length) {
    const alter = displayAlterations.value[0]
    await fetchTreatments(distance, location, alter, alter.gene, alter.originalAlt)
    await fetchTrials(distance, location, alter, alter.gene, alter.originalAlt)
  }
}

const fetchTreatments = async (distance, location, alter, gene, alt) => {
  const _treatments = await getTreatments(distance, location, gene, alt)
  alter.treatmentsCount = _treatments.total
  treatments.value.push(_treatments.rows)
  alter.treatmentRows = _treatments.rows

}

const fetchTrials = async (distance, location, alter, gene, alt) => {
  const _trials = await getTrials(distance, location, gene, alt)
  alter.trialsCount = _trials.total
  trials.value.push(_trials.rows)
  alter.trialRows = _trials.rows
}

const getTMBMSI = async () => {
  //MSI
  const msiResults = await getTmbMsi(sampleId.value, 'MSI')
  msi.value = msiResults.results[0]?.value || 'No clinically significant MSI'

  //TMB
  const tmbResults = await getTmbMsi(sampleId.value, 'TMB')
  tmb.value = tmbResults.results[0]?.value || 'No clinically significant TMB'
}

const recodeAa = (p) => {
  p = p.replace(/Ala/g, 'A')
  p = p.replace(/Arg/g, 'R')
  p = p.replace(/Asp/g, 'D')
  p = p.replace(/Asn/g, 'N')
  p = p.replace(/Cys/g, 'C')
  p = p.replace(/Glu/g, 'E')
  p = p.replace(/Gln/g, 'Q')
  p = p.replace(/Gly/g, 'G')
  p = p.replace(/His/g, 'H')
  p = p.replace(/Ile/g, 'I')
  p = p.replace(/Leu/g, 'L')
  p = p.replace(/Lys/g, 'K')
  p = p.replace(/Met/g, 'M')
  p = p.replace(/Phe/g, 'F')
  p = p.replace(/Pro/g, 'P')
  p = p.replace(/Ser/g, 'S')
  p = p.replace(/Thr/g, 'T')
  p = p.replace(/Trp/g, 'W')
  p = p.replace(/Tyr/g, 'Y')
  p = p.replace(/Val/g, 'V')
  p = p.replace(/Ter/g, '*')
  return (p)
}

onMounted(async () => {
  await loadAllGenomics()
})

watch(sampleId, async () => {
  if (sampleId.value === sampleIdStored.value) { return }
  sampleIdStored.value = sampleId.value
  await loadAllGenomics()
})

</script>

<style lang="scss" scoped>
.genomics-data {
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
}

.trials-container {
  margin-top: 20px;
  width: 100%;
  margin-left: -80px;
}

.variant-card {
  display: none;
}

.variant-card-active {
  display: flex;
}

.alterations {
  height: 400px;
  width: 305px;
  overflow: auto;
}

.base-card {
  border-radius: 7px;
  box-shadow: 0 0 3px #b9b9b9;
  background-color: var(--White);
  overflow: hidden;
  position: relative;
}

.card-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 1.4rem;
  color: var(--Grey600);
  --angle: 90deg;
  border-bottom: 1px solid;
  border-image: conic-gradient(from var(--angle), var(--MarketingMagenta),
      var(--BlendedOrange), var(--MarketingOrange), var(--BlendedOrange),
      var(--MarketingMagenta)) 1;
  transition: all linear 300ms;

  &>.card-icon {
    width: 1.4rem;
    cursor: pointer;
    padding-top: 3px;
  }

}

.variants-container {
  font-size: 70%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 50%;
  height: 80%;
  margin-top: 30px;
  flex-shrink: 0;
  overflow: auto;
}

.tier-tabs-container {
  display: flex;
  margin-left: 35px;
}

.tier-text {
  margin-left: 5px;
  vertical-align: middle;
}

.significance-label {
  margin: 20px 20px 20px 50px;
  color: var(--Black);
}

.tier-tabs {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 5px;
  margin-right: 30px;
  background-color: var(--Grey100);
  padding: 8px;
  border-radius: 5px;
  text-align: center;
  color: var(--Grey600);
  user-select: none;
  font-size: 90%;

  cursor: pointer;

  &:hover {
    color: var(--Black);
  }
}

.tier-tab-active {
  color: var(--Black)
}

.items {
  margin: 10px 5px 5px 5px;
  padding-left: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.view-report {
  margin: 10px 5px 15px;
  padding-left: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.header-middle {
  display: flex;
  flex-direction: row;
}

.header-container {
  transform: scale(.70);
  border-radius: 10px;
  background-color: #F7F9FB;
  overflow: auto;
  position: relative;
  text-align: left;
  margin: 0% -16% -6% -16%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

.header-top-first {
  flex-direction: row;
  margin-bottom: 5px;
}

.datum {
  margin: 5%;
  display: flex;
  flex-direction: column;
}

.genomics {
  width: max-content;
  border-radius: 4px;
  background-color: #e9ffaa;
  color: var(--Grey700);
  font-size: 80%;
  margin: 2px;
  padding: 5px;
  margin-left: 15px;
}

.label {
  color: var(--Grey500);
  font-size: 70%;
  text-transform: uppercase;
}

.location {
  color: var(--Grey500);
  margin: -2% 5% 2% 5%;
  display: flex;
  flex-direction: row;
  font-size: 90%;
}

.location-icon {
  padding-bottom: 3px;
}

.location-text {
  padding-top: 3px;
}

.specimen-type-text {
  font-weight: bold;
}

.selected {
  border: 2px solid;
}

.tier-label {
  font-weight: bold;
}
</style>



<style type="text/css" scoped>
.spinner {
  --angle: 360deg;
  border-bottom: 1vmin solid;
  border-image: conic-gradient(from var(--angle), #e901be, #f46d4e, #ffaa0f, #f46d4e, #e901be) 1;
  animation: 1s rotate linear infinite;
}

@keyframes rotate {
  to {
    --angle: 0deg;
  }
}

@property --angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: true;
}
</style>
